import { useState, useEffect, useRef } from 'react'

// Reusable multi-select filter. `groups` = [{ key, label, options: [...] }].
// Value is { [groupKey]: [selected...] }. Empty/absent group = all shown.
export function useTableFilter(sectionKey, groups) {
  const storeKey = `filter_${sectionKey}`
  const [filter, setFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storeKey) || '{}') } catch { return {} }
  })
  useEffect(() => {
    try { localStorage.setItem(storeKey, JSON.stringify(filter)) } catch {}
  }, [storeKey, filter])

  const toggle = (gk, val) => setFilter(prev => {
    const cur = prev[gk] || []
    const next = cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val]
    return { ...prev, [gk]: next }
  })
  const clearAll = () => setFilter({})

  // a row passes if, for every group with selections, its value is included
  const matches = (row, accessors) => groups.every(g => {
    const sel = filter[g.key]
    if (!sel || sel.length === 0) return true
    const val = accessors[g.key] ? accessors[g.key](row) : (row[g.key] ?? '')
    return sel.includes(val || '')
  })

  const activeCount = groups.reduce((a, g) => a + ((filter[g.key] || []).length ? 1 : 0), 0)
  return { filter, toggle, clearAll, matches, activeCount }
}

export function FilterButton({ groups, filter, toggle, clearAll, activeCount }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])
  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button className={activeCount ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setOpen(o => !o)}>
        ⚑ Filter{activeCount ? ` (${activeCount})` : ''}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', right: 0, zIndex: 30, background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 10, padding: 12, minWidth: 210, boxShadow: '0 8px 28px rgba(0,0,0,0.45)', maxHeight: 420, overflowY: 'auto' }}>
          {groups.map(g => (
            <div key={g.key} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.6px', fontFamily: 'DM Mono,monospace', marginBottom: 6 }}>{g.label}</div>
              {g.options.map(opt => {
                const sel = (filter[g.key] || []).includes(opt.value)
                return (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 2px', fontSize: 12, cursor: 'pointer' }}>
                    <input type="checkbox" checked={sel} onChange={() => toggle(g.key, opt.value)} style={{ width: 14, height: 14 }} />
                    {opt.label}
                  </label>
                )
              })}
            </div>
          ))}
          {activeCount > 0 && (
            <button className="btn btn-ghost" style={{ fontSize: 10, padding: '3px 8px', width: '100%' }} onClick={clearAll}>Clear all filters</button>
          )}
        </div>
      )}
    </div>
  )
}
